/*
  Maison Fermoir — garde-fou du cadeau, 23/09/2026.

  CE QU'IL CORRIGE, et c'est mesuré. Fullstack sait déjà repérer une ligne de cadeau
  illégitime : `auto-gift-processor.js` porte `findUnauthorizedGifts()`, qui compare le
  `product_id` de chaque ligne à `window.__giftOnlyProducts` et supprime celles qui ne
  portent pas la propriété `_is_gift`.

  ⚠ MAIS `processGifts()` n'est appelé QU'APRÈS UNE MODIFICATION DU PANIER : dans le
  `.finally()` d'un changement de quantité (`cart.js`) et au re-rendu du tiroir
  (`cart-drawer.js`). RIEN ne le lance au simple chargement d'une page. Reproduit sur le
  live le 23/09 : cinq bracelets à 0,00 € injectés par `fetch('/cart/add.js')`, puis
  `/cart` ouvert — les cinq lignes étaient toujours là, avec le bouton Commander actif.

  Ce fichier comble ce seul trou : il rejoue le contrôle AU CHARGEMENT de chaque page.

  Il fait deux choses, et rien d'autre :
    1. il supprime toute ligne d'un produit réservé au cadeau qui ne porte pas `_is_gift` ;
    2. il ramène à 1 toute ligne de cadeau dont la quantité a été gonflée.

  🔑 IL NE CONNAÎT AUCUN IDENTIFIANT DE PRODUIT. Il lit `window.__giftOnlyProducts`, que le
  bloc `_auto-gift` publie déjà quand « restrict_direct_purchase » est coché. Changer de
  cadeau ne demande donc aucune retouche ici. ⚠ Si cette case est décochée, l'objet
  n'existe pas et ce script ne fait rien — c'est volontaire, il suit le réglage.

  ⚠ CE QU'IL NE FAIT PAS, et il faut le savoir : il s'exécute dans le navigateur. Il arrête
  tout ce qui passe par une page (console, extension, lien fabriqué), mais pas un script qui
  irait au paiement sans jamais charger le site. La seule fermeture étanche est une remise
  par fonction Shopify, qui suppose une app. Le filet d'après, c'est Shopify Flow, qui met
  en attente une commande suspecte APRÈS coup.
*/

(() => {
  const CLE_BOUCLE = 'mf-garde-cadeau-rechargement';

  const reservesAuCadeau = () => window.__giftOnlyProducts || null;

  async function panier() {
    const r = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!r.ok) throw new Error(`cart.js a rendu ${r.status}`);
    return r.json();
  }

  async function corriger(cle, quantite) {
    const r = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: cle, quantity: quantite }),
    });
    if (!r.ok) throw new Error(`cart/change.js a rendu ${r.status}`);
    return r.json();
  }

  async function controler() {
    const reserves = reservesAuCadeau();
    if (!reserves || Object.keys(reserves).length === 0) return;

    let etat;
    try {
      etat = await panier();
    } catch (e) {
      console.warn('[mf-garde-cadeau] panier illisible :', e.message);
      return;
    }
    if (!etat.items || etat.items.length === 0) return;

    // On collecte AVANT de corriger : chaque appel à cart/change.js réordonne les lignes,
    // et une boucle qui corrige en itérant sur la liste d'origine vise les mauvaises clés.
    const aRetirer = [];
    const aRamenerA1 = [];

    for (const ligne of etat.items) {
      if (!reserves[ligne.product_id]) continue;
      const estUnVraiCadeau = ligne.properties?._is_gift === 'true';
      if (!estUnVraiCadeau) {
        aRetirer.push(ligne);
      } else if (ligne.quantity > 1) {
        aRamenerA1.push(ligne);
      }
    }

    if (aRetirer.length === 0 && aRamenerA1.length === 0) {
      sessionStorage.removeItem(CLE_BOUCLE);
      return;
    }

    for (const ligne of aRetirer) {
      try {
        await corriger(ligne.key, 0);
        console.info(`[mf-garde-cadeau] ligne non autorisée retirée : ${ligne.title} x${ligne.quantity}`);
      } catch (e) {
        console.warn('[mf-garde-cadeau] retrait impossible :', e.message);
      }
    }

    for (const ligne of aRamenerA1) {
      try {
        await corriger(ligne.key, 1);
        console.info(`[mf-garde-cadeau] quantité de cadeau ramenée à 1 : ${ligne.title}`);
      } catch (e) {
        console.warn('[mf-garde-cadeau] correction de quantité impossible :', e.message);
      }
    }

    // ⚠ Le panier affiché vient du HTML déjà rendu : sans rechargement, la cliente voit
    //   encore les lignes qu'on vient de retirer. On ne recharge qu'UNE fois, pour qu'une
    //   correction qui échouerait ne mette pas la page en boucle.
    if (sessionStorage.getItem(CLE_BOUCLE) === '1') {
      console.warn('[mf-garde-cadeau] correction déjà tentée sur cette page, pas de second rechargement');
      return;
    }
    sessionStorage.setItem(CLE_BOUCLE, '1');
    window.location.reload();
  }

  // ⚠ `__giftOnlyProducts` est posé par un <script type="module">, donc DIFFÉRÉ : il n'existe
  //   pas encore à DOMContentLoaded. On attend `load`, et on laisse quelques essais courts
  //   au cas où le module arriverait juste après.
  function demarrer() {
    let essais = 0;
    const tenter = () => {
      if (reservesAuCadeau() || essais >= 10) {
        controler();
        return;
      }
      essais += 1;
      setTimeout(tenter, 150);
    };
    tenter();
  }

  if (document.readyState === 'complete') demarrer();
  else window.addEventListener('load', demarrer, { once: true });

  // Retour par le bouton « précédent » : la page sort du cache sans rejouer `load`.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) demarrer();
  });
})();
